/**
 * Product Launch Countdown JavaScript
 *
 * This script handles real-time countdown updates for product launch dates.
 * It updates buttons and status text when launch dates are reached.
 */

// Global variables for countdown management
let launchCountdownIntervals = {};
let launchCountdownElements = {};

/**
 * Initialize launch countdown functionality
 */
function initializeLaunchCountdowns() {
  // Clear any existing intervals
  Object.values(launchCountdownIntervals).forEach((interval) => {
    if (interval) {
      clearInterval(interval);
    }
  });
  launchCountdownIntervals = {};
  launchCountdownElements = {};

  // Find all elements with launch countdown data
  document.querySelectorAll('[data-launch-countdown]').forEach((element) => {
    const productId = element.getAttribute('data-product-id');
    const launchDate = element.getAttribute('data-launch-date');

    if (productId && launchDate) {
      launchCountdownElements[productId] = element;
      startLaunchCountdown(productId, launchDate);
    }
  });

  // Also check for elements that might be added dynamically
  setupLaunchCountdownObserver();
}

/**
 * Start countdown for a specific product
 */
function startLaunchCountdown(productId, launchDateString) {
  const element = launchCountdownElements[productId];
  if (!element) return;

  // Clear any existing interval for this product
  if (launchCountdownIntervals[productId]) {
    clearInterval(launchCountdownIntervals[productId]);
  }

  const launchDate = new Date(launchDateString);
  const now = new Date();

  // If launch date is in the past, update immediately
  if (launchDate <= now) {
    updateLaunchStatus(productId);
    return;
  }

  // Update countdown every second
  launchCountdownIntervals[productId] = setInterval(() => {
    const currentTime = new Date();
    if (currentTime >= launchDate) {
      // Launch time reached, update status and stop countdown
      updateLaunchStatus(productId);
      clearInterval(launchCountdownIntervals[productId]);
      delete launchCountdownIntervals[productId];
    } else {
      // Update countdown display
      updateCountdownDisplay(productId, launchDate, currentTime);
    }
  }, 1000);

  // Initial update
  updateCountdownDisplay(productId, launchDate, now);
}

/**
 * Update countdown display
 */
function updateCountdownDisplay(productId, launchDate, currentTime) {
  const element = launchCountdownElements[productId];
  if (!element) return;

  const timeRemaining = launchDate - currentTime;

  // Calculate days, hours, minutes, seconds
  const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  // Update display if element has countdown text
  const countdownText = element.querySelector('.launch-countdown-text');
  if (countdownText) {
    countdownText.textContent = `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }
}

/**
 * Update launch status when countdown reaches zero
 */
function updateLaunchStatus(productId) {
  const element = launchCountdownElements[productId];
  if (!element) return;

  // Find all buttons and status elements for this product
  const productButtons = document.querySelectorAll(
    `[data-product-id="${productId}"] [name="add"]`
  );
  const statusElements = document.querySelectorAll(
    `[data-product-id="${productId}"] .product-card-status-text`
  );

  // Update button text to "Add to Cart" if it was disabled for prerelease
  productButtons.forEach((button) => {
    const buttonText = button.querySelector('span');
    if (buttonText && button.hasAttribute('disabled')) {
      // Check if it was disabled due to prerelease
      const productCard = button.closest('[data-product-id]');
      if (productCard && productCard.hasAttribute('data-prerelease')) {
        button.removeAttribute('disabled');
        buttonText.textContent = window.variantStrings
          ? window.variantStrings.addToCart
          : 'Add to Cart';
      }
    }
  });

  // Update status text
  statusElements.forEach((statusElement) => {
    const comingSoonText = statusElement.querySelector('span:first-child');
    const dateText = statusElement.querySelector('span:last-child');

    if (comingSoonText && dateText) {
      // Get the original launch date from the countdown element
      const countdownElement = document.querySelector(
        `[data-product-id="${productId}"][data-launch-countdown]`
      );
      if (countdownElement) {
        const launchDateString =
          countdownElement.getAttribute('data-launch-date');
        const launchDate = new Date(launchDateString);
        const now = new Date();

        comingSoonText.textContent =
          window.translationStrings &&
          window.translationStrings.launches_today
            ? window.translationStrings.launches_today
            : 'Launches today';
      } else {
        comingSoonText.textContent =
          window.translationStrings && window.translationStrings.launches_today
            ? window.translationStrings.launches_today
            : 'Launches today';
      }
    }
  });

  // Remove the countdown element
  if (element.hasAttribute('data-launch-countdown')) {
    element.remove();
  }
}

/**
 * Setup observer for dynamically added elements
 */
function setupLaunchCountdownObserver() {
  const observer = new MutationObserver((mutations) => {
    let shouldReinitialize = false;

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if the added node has launch countdown data
          if (node.hasAttribute && node.hasAttribute('data-launch-countdown')) {
            shouldReinitialize = true;
          }
          // Check if any child elements have launch countdown data
          const countdownElements = node.querySelectorAll
            ? node.querySelectorAll('[data-launch-countdown]')
            : [];
          if (countdownElements.length > 0) {
            shouldReinitialize = true;
          }
        }
      });
    });

    if (shouldReinitialize) {
      // Small delay to ensure all elements are fully added
      setTimeout(() => {
        initializeLaunchCountdowns();
      }, 100);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * Initialize on DOM content loaded
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeLaunchCountdowns);
} else {
  initializeLaunchCountdowns();
}

/**
 * Reinitialize countdowns after cart updates or other dynamic changes
 */
document.addEventListener('cart:updated', initializeLaunchCountdowns);
document.addEventListener('product:updated', initializeLaunchCountdowns);
